import { Icon } from '@/components/Icon';
import { SeverityBadge } from '@/components/primitives';
import { RequireRole } from '@/components/RequireRole';
import { ChannelChip } from '@/components/alerts/ChannelChip';
import { findChannel } from '@/lib/channels';
import { CONDITIONS, conditionSummary, scopeLabel } from '@/components/alerts/conditions';
import type { NotificationChannel, NotificationRule } from '@/types/sturm';

interface RuleCardProps {
  rule: NotificationRule;
  channels: NotificationChannel[];
  workflowNames?: Record<string, string>;
  compact?: boolean;
  onToggle?: () => void;
  onEdit?: () => void;
}

export function RuleCard({
  rule,
  channels,
  workflowNames = {},
  compact,
  onToggle,
  onEdit,
}: RuleCardProps) {
  const condition = CONDITIONS.find((c) => c.id === rule.condition);

  return (
    <div className={`rule ${rule.isEnabled ? '' : 'is-off'}`}>
      <div style={{ minWidth: 0 }}>
        <div className="row gap-8">
          <div className="rule-name">{rule.name}</div>
          <SeverityBadge severity={rule.severity} />
        </div>
        <div className="rule-line">
          <span
            className={`cond-chip ${
              rule.severity === 'critical' ? 'crit' : rule.severity === 'warning' ? 'warnl' : ''
            }`}
          >
            <Icon name={condition?.icon ?? 'alert'} size={12} />
            {conditionSummary(rule)}
          </span>
          {!compact ? (
            <>
              <span className="rule-arrow">
                <Icon name="arrowRight" size={13} />
              </span>
              <span className="cond-chip">
                <Icon name="flow" size={12} />
                {scopeLabel(rule.scope, workflowNames)}
              </span>
            </>
          ) : null}
          <span className="rule-arrow">
            <Icon name="arrowRight" size={13} />
          </span>
          {rule.channelIds.map((id) => (
            <ChannelChip key={id} channel={findChannel(channels, id)} />
          ))}
        </div>
        <div className="rule-foot">
          <span className="row gap-6">
            <Icon name="layers" size={12} /> Group by {rule.groupBy}
          </span>
          <span className="row gap-6">
            <Icon name="clock" size={12} /> Throttle {rule.throttle}
          </span>
          {rule.autoResolve ? (
            <span className="row gap-6">
              <Icon name="check" size={12} /> Auto-resolve
            </span>
          ) : null}
          <span className="row gap-6" style={{ marginLeft: 'auto' }}>
            <strong
              style={{
                color: rule.fired24h ? 'var(--ink-900)' : 'var(--ink-400)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {rule.fired24h}
            </strong>
            fired · 24h
          </span>
          <span>last {rule.lastFired}</span>
        </div>
      </div>
      <div className="rule-side">
        <RequireRole min="manager">
          <button className={`switch ${rule.isEnabled ? 'on' : ''}`} onClick={onToggle}>
            <span className="knob" />
          </button>
          <div className="row gap-6">
            <button className="btn btn-sm btn-secondary" onClick={onEdit}>
              <Icon name="settings" size={12} /> Edit
            </button>
          </div>
        </RequireRole>
      </div>
    </div>
  );
}
